import { ChatEntity } from "@/indexedDB/types";

import {
  archiveChat,
  muteChat,
  pinChat,
  unarchiveChat,
  unmuteChat,
  unpinChat,
} from "@/middleware/chats";
import { ContextMenuContent, ContextMenuItem } from "@primitives/context-menu";
import { useTranslation } from "react-i18next";

import { MemberRole } from "@/server/types/member";

import { LogOut, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { VolumeOffIcon, Volume2Icon, Pin } from "lucide-react";

import { ChatType } from "@/server/types/chat";

import { useToast } from "@/hooks/useToast";

import { IS_MOBILE } from "@/lib/environmentDetector";
import { useState } from "react";
import DeleteP2PChat from "@/components/shared/dialogs/DeleteP2PChat";
import DeleteGroupChat from "@/components/shared/dialogs/DeleteGroupChat";
import LeaveGroupChat from "@/components/shared/dialogs/LeaveGroupChat";

type ActionButton = {
  icon: React.ReactNode;
  label: string;
  style?: string;
  onClick?: () => Promise<void>;
};

export default function ChatListEntryMenu({ chat }: { chat: ChatEntity }) {
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState<
    "delete-p2p-chat" | "delete-group-chat" | "leave-group-chat" | null
  >(null);

  const { toast } = useToast();
  const handleMuteChat = async () => {
    try {
      await muteChat(chat.id);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handleUnMuteChat = async () => {
    try {
      await unmuteChat(chat.id);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handlePinChat = async () => {
    try {
      await pinChat(chat.id);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handleUnPinChat = async () => {
    try {
      await unpinChat(chat.id);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handleArchiveChat = async () => {
    try {
      await archiveChat(chat.id);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handleUnarchiveChat = async () => {
    try {
      await unarchiveChat(chat.id);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  // TO DO => to destructure (a bit hard to follow)
  const actionButtons: ActionButton[] = [
    {
      icon: <Pin className="mr-2 size-4" />,
      label: chat.isPinned ? t("unpin") : t("pin"),
      onClick: async () => {
        if (!chat.isPinned) {
          handlePinChat();
        }
        if (chat.isPinned) {
          handleUnPinChat();
        }
      },
    },
    {
      icon: chat.isArchived ? (
        <ArchiveRestore className="mr-2 size-4" />
      ) : (
        <Archive className="mr-2 size-4" />
      ),
      label: chat.isArchived ? t("unarchive") : t("archive"),
      onClick: async () => {
        if (!chat.isArchived) {
          handleArchiveChat();
        }
        if (chat.isArchived) {
          handleUnarchiveChat();
        }
      },
    },
    {
      icon: chat.isMuted ? (
        <VolumeOffIcon className="mr-2 size-4" />
      ) : (
        <Volume2Icon className="mr-2 size-4" />
      ),
      label: chat.isMuted ? t("unmute") : t("mute"),
      onClick: async () => {
        if (!chat.isMuted) {
          handleMuteChat();
        }
        if (chat.isMuted) {
          handleUnMuteChat();
        }
      },
    },
    {
      icon:
        chat.type === ChatType.P2P ? (
          <Trash2 className="mr-2 size-4" />
        ) : chat.type === ChatType.GROUP &&
          chat.roleAsMember.role === MemberRole.OWNER ? (
          <Trash2 className="mr-2 size-4" />
        ) : (
          <LogOut className="mr-2 size-4" />
        ),
      style: "text-destructive",
      label:
        chat.type === ChatType.P2P
          ? t("delete")
          : chat.type === ChatType.GROUP &&
              chat.roleAsMember.role === MemberRole.OWNER
            ? t("deleteGroup")
            : t("leave"),
      onClick: async () => {
        if (chat.type === ChatType.P2P) {
          setTimeout(() => {
            setOpenDialog("delete-p2p-chat");
          }, 0);
          return;
        } else if (
          chat.type === ChatType.GROUP &&
          chat.roleAsMember.role === MemberRole.OWNER
        ) {
          setTimeout(() => {
            setOpenDialog("delete-group-chat");
          }, 0);
          return;
        } else {
          setTimeout(() => {
            setOpenDialog("leave-group-chat");
          }, 0);

          return;
        }
      },
    },
  ];

  return (
    <>
      <ContextMenuContent
        collisionPadding={10}
        backdrop={IS_MOBILE ? "blur" : "transparent"}
        className="border-none shadow-md outline-none"
      >
        {actionButtons.map((button, index) => (
          <ContextMenuItem
            key={index}
            onClick={button.onClick}
            className={button.style ?? button.style}
          >
            {button.icon} <span>{button.label}</span>
          </ContextMenuItem>
        ))}
      </ContextMenuContent>

      {/* CONFIRMATION DIALOGS */}

      <DeleteP2PChat
        chatId={chat.id}
        open={openDialog === "delete-p2p-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-p2p-chat" : null);
        }}
      />

      <DeleteGroupChat
        chatId={chat.id}
        open={openDialog === "delete-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />

      <LeaveGroupChat
        chatId={chat.id}
        open={openDialog === "leave-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "leave-group-chat" : null);
        }}
      />
    </>
  );
}
